import axios from '@fs/zion-axios'

const BASE_PERSONS_PATH = '/service/dmc/persons'

export async function getPerson(personId, forward) {
  if (personId) {
    const response = await axios.get(`${BASE_PERSONS_PATH}/${personId}/summary${forward ? `?forward=${forward}` : ''}`)
    return response.status >= 200 && response.status < 300 ? response.data : null
  }
  return null
}

export async function getPersonSummary(personId, forward) {
  if (personId) {
    const response = await axios.get(`${BASE_PERSONS_PATH}/${personId}${forward ? `?forward=${forward}` : ''}`)
    return response.status >= 200 && response.status < 300 ? response.data : null
  }
  return null
}

export async function getPersonName(personId) {
  if (personId) {
    const response = await axios.get(`${BASE_PERSONS_PATH}/${personId}/name`)
    return response.status >= 200 && response.status < 300 ? response.data : null
  }
  return null
}

function putPersonConclusion(personId, resourceType, conclusion, changeMessage) {
  return axios({
    method: 'put',
    url: `${BASE_PERSONS_PATH}/${personId}/${resourceType}/${conclusion.conclusionId}`,
    data: conclusion,
    headers: {
      'X-Reason': changeMessage,
    },
  })
}

export function getMissingRelationshipOwnerInfo(dmcPerson) {
  return axios({
    method: 'post',
    url: `${BASE_PERSONS_PATH}/missingRelationshipOwnerInfo`,
    data: dmcPerson,
  })
}

export async function getPersonsSummaries(personIds) {
  if (!personIds || !personIds.length > 0) {
    return Promise.reject()
  }
  const queryString = `?personId=${personIds.join('&personId=')}`
  return axios.get(`${BASE_PERSONS_PATH}/personsSummaries${queryString}`).then((response) => {
    return response.data
  })
}

export async function getPersonCard(personId) {
  return axios.get(`${BASE_PERSONS_PATH}/personCard?personId=${personId}`).then((response) => {
    return response.data
  })
}

export async function putNameConclusion(personId, nameConclusion) {
  const response = await putPersonConclusion(personId, 'names', nameConclusion)
  return response
}

export async function putGenderConclusion(personId, genderConclusion) {
  const response = await putPersonConclusion(personId, 'gender', genderConclusion)
  return response
}

export async function putFactConclusion(personId, factConclusion, changeMessage) {
  const response = await putPersonConclusion(personId, 'facts', factConclusion, changeMessage)
  return response
}

export async function postFactConclusion(personId, factConclusion) {
  const response = await axios.post(`${BASE_PERSONS_PATH}/${personId}/facts`, factConclusion)
  return response
}

export async function deleteFact(personId, conclusionid, changeMessage) {
  const response = await axios({
    method: 'delete',
    url: `${BASE_PERSONS_PATH}/${personId}/facts/${conclusionid}`,
    headers: {
      'X-Reason': changeMessage,
    },
  })
  return response
}

// either pass ownerCisId or ownerGroupId, not both
export async function makePersonLiving(personId, ownerCisId, ownerTreeId, changeMessage) {
  const requestBody = {
    ownerCisId,
    ownerTreeId,
    changeMessage,
  }
  const response = await axios.put(`${BASE_PERSONS_PATH}/${personId}/make-living`, requestBody)
  return response
}

export async function tombstonePerson(personId, changeMessage) {
  const response = await axios({
    method: 'delete',
    url: `${BASE_PERSONS_PATH}/${personId}`,
    headers: {
      'X-Reason': changeMessage,
    },
  })
  return response
}

export async function restorePerson(personId, changeMessage) {
  const requestBody = {
    changeMessage,
  }
  return axios.put(`${BASE_PERSONS_PATH}/${personId}/restore`, requestBody)
}

export async function getAccessControl(personId) {
  const response = await axios.get(`${BASE_PERSONS_PATH}/${personId}/accesscontrol`)
  return response
}

export async function updateAccessControl(personId, data) {
  const response = await axios.put(`${BASE_PERSONS_PATH}/${personId}/accesscontrol`, data)
  return response
}

export async function getOrdinanceData(personId) {
  const response = await axios.get(`${BASE_PERSONS_PATH}/${personId}/ordinances`)
  return response
}

export async function getReservationData(personId) {
  const response = await axios.get(`${BASE_PERSONS_PATH}/${personId}/reservations`)
  return response
}

export function getPersonReservationHistory(personId) {
  return axios.get(`${BASE_PERSONS_PATH}/${personId}/reservations-history-dmc`)
}

export function getReservationHistoryQuery(executionId) {
  return axios.get(`${BASE_PERSONS_PATH}/reservations-history/query/${executionId}`)
}

export async function getPersonCopies(personId) {
  const response = await axios.get(`${BASE_PERSONS_PATH}/${personId}/copies`)
  return response
}

export async function getSources(personId) {
  const response = await axios.get(`${BASE_PERSONS_PATH}/${personId}/sources`)
  return response
}

export async function findCommonAncestorToUser(personId, userId, includeAllMePersons) {
  return axios
    .get(`${BASE_PERSONS_PATH}/${personId}/commonAncestorToUser/${userId}?includeAllMePersons=${includeAllMePersons}`)
    .then((response) => {
      return response.data
    })
}

export async function calculateRelationship(
  leftPersonId,
  rightPersonId,
  hintCommonAncestorIds,
  securityType,
  traversalType,
  includeAllMePersons
) {
  const body = { sourceIds: [leftPersonId], relativeIds: [rightPersonId] }
  const params = {
    hintCommonAncestorId: hintCommonAncestorIds.join(','),
    includeAllMePersons,
    securityType,
    traversalType,
  }
  const response = await axios.post(`${BASE_PERSONS_PATH}/calculateRelationship`, body, { params })
  return response.data
}

export function getPersonLabels(personId) {
  return axios.get(`${BASE_PERSONS_PATH}/${personId}/labels`).then((response) => {
    return response.data
  })
}

export function getRelationshipsAccessControls(relationships, personIds) {
  if (personIds) {
    return axios.post(`${BASE_PERSONS_PATH}/tombstonedRelationships/accesscontrols`, Array.from(personIds))
  }
  return axios.post(`${BASE_PERSONS_PATH}/relationships/accesscontrols`, relationships)
}

export async function getCmisReferences({ id }) {
  const response = await axios.get(`${BASE_PERSONS_PATH}/${id}/references`)
  return response
}

export async function addCmisReference(personId, mrn) {
  const response = await axios.post(`${BASE_PERSONS_PATH}/${personId}/references`, { mrn })
  return response
}

export function deleteCmisReferences(personIdRefObject, changeMessage) {
  return axios({
    method: 'delete',
    url: `${BASE_PERSONS_PATH}/${personIdRefObject.personId}/references`,
    data: personIdRefObject.entityRefIds,
    headers: {
      'X-Reason': changeMessage,
    },
  })
}

export async function getMergeHistory(personId, includeDuplicateOwnerInfo = false) {
  const response = await axios.get(
    `${BASE_PERSONS_PATH}/${personId}/merges?includeDuplicateOwnerInfo=${includeDuplicateOwnerInfo}`
  )
  return response
}

export async function getMergeChainEndPersonId(personId) {
  const response = await axios.get(`${BASE_PERSONS_PATH}/${personId}/mergechain/end`)
  return response
}

export function getMergeConstraints(survivingPersonId, possibleDuplicateId) {
  return axios.get(`${BASE_PERSONS_PATH}/${survivingPersonId}/merge/${possibleDuplicateId}/constraints`)
}

export async function getPersonChanges(personId, fromChangeId) {
  let url = `${BASE_PERSONS_PATH}/${personId}/changes`
  if (fromChangeId) {
    url = `${url}?fromChangeId=${fromChangeId}`
  }
  const response = await axios.get(url)
  return response
}

export async function getLatestPersonChange(personId) {
  const response = await axios.get(`${BASE_PERSONS_PATH}/${personId}/changes/latest`)
  return response
}

export async function getPersonContributors(personId) {
  const response = await axios.get(`${BASE_PERSONS_PATH}/${personId}/contributors`)
  return response
}

export async function getFilteredPersonChanges(personId, type, subtypes) {
  const url = `${BASE_PERSONS_PATH}/${personId}/filteredChanges?type=${type}&subtype=${subtypes.join('&subtype=')}`
  const response = await axios.get(url)
  return response
}

export async function toggleVisible(hidden, targetEntityType, targetEntityId, changeId) {
  const url = `${BASE_PERSONS_PATH}/toggleVisible`
  const response = await axios.put(url, { hidden, targetEntityType, targetEntityId, changeId })
  return response
}

export async function toggleAllVisible(changeMetadataList) {
  const url = `${BASE_PERSONS_PATH}/toggleAllVisible`
  const response = await axios.put(url, changeMetadataList)
  return response
}

export function mergePersons(possibleDuplicateId, survivingPersonId, mergedFieldIds) {
  return axios.put(`${BASE_PERSONS_PATH}/${survivingPersonId}/merge/${possibleDuplicateId}`, mergedFieldIds)
}

export async function undoMerge(personId, changeId, reason) {
  const response = await axios.put(`${BASE_PERSONS_PATH}/${personId}/merges/${changeId}`, { changeMessage: reason })
  return response
}

export function removeRelationship(relationshipId, relationshipType, changeMessage) {
  return axios({
    method: 'delete',
    url: `${BASE_PERSONS_PATH}/relationships/${relationshipType}/${relationshipId}`,
    headers: {
      'X-Reason': changeMessage,
    },
  })
}

export function removeRelationships(relationships, reason) {
  const relationship = relationships.pop()
  if (relationships.length > 0) {
    return removeRelationship(relationship.id, relationship.type, reason).then(() =>
      removeRelationships(relationships, reason)
    )
  }
  return removeRelationship(relationship.id, relationship.type, reason)
}

export function restoreRelationship(relationshipId, relationshipType, changeMessage) {
  const requestBody = {
    changeMessage,
  }
  return axios.put(`${BASE_PERSONS_PATH}/relationships/${relationshipType}/${relationshipId}`, requestBody)
}

export function restoreRelationships(relationships, reason) {
  const relationship = relationships.pop()
  if (relationships.length > 0) {
    return restoreRelationship(relationship.id, relationship.type, reason).then(() =>
      restoreRelationships(relationships, reason)
    )
  }
  return restoreRelationship(relationship.id, relationship.type, reason)
}

export async function getPersonOtherInfo(personId) {
  const response = await axios.get(`${BASE_PERSONS_PATH}/${personId}/other-info`)
  return response
}

export async function deleteOtherInfo(personId, conclusionIds, changeMessage) {
  const setup = '?conclusionId='
  const glue = '&conclusionId='
  let finalQuery = ''

  finalQuery = setup + conclusionIds[0]

  if (conclusionIds.length > 1) {
    conclusionIds.forEach((id) => {
      if (id !== conclusionIds[0]) {
        finalQuery = finalQuery + glue + id
      }
    })
  }

  const response = await axios({
    method: 'delete',
    url: `${BASE_PERSONS_PATH}/${personId}/other-info/${finalQuery}`,
    headers: {
      'X-Reason': changeMessage,
    },
  })
  return response
}

export async function getPersonLabelMetadata() {
  return axios.get(`${BASE_PERSONS_PATH}/labels/metadata`).then((response) => {
    return response.data
  })
}

export async function addAdminLabel(personId, label) {
  const response = await axios.post(`${BASE_PERSONS_PATH}/${personId}/labels`, label)
  return response
}

export async function editAdminLabel(labelId, changeMessage) {
  return axios.put(`${BASE_PERSONS_PATH}/labels/${labelId}`, { reason: changeMessage }).then((response) => {
    return response.data
  })
}

export async function deleteAdminLabel(personId, labelId, changeMessage) {
  return axios({
    method: 'delete',
    url: `${BASE_PERSONS_PATH}/${personId}/labels/${labelId}`,
    headers: {
      'X-Reason': changeMessage,
    },
  }).then((response) => {
    return response.data
  })
}

export function addCtrs(ordinanceList) {
  return axios.post(`${BASE_PERSONS_PATH}/createCtrs`, ordinanceList)
}

export function createTreeDataPerson(person) {
  return axios.post(`${BASE_PERSONS_PATH}/tree-entity/person`, person, { params: { returnPersonData: true } })
}

export function createTreeDataCouple(personId, spouseId, spouseGender) {
  const params = {
    personId,
    spouseId,
    spouseGender,
  }
  return axios.post(`${BASE_PERSONS_PATH}/tree-entity/couple`, {}, { params })
}

export function createTreeDataParentChildRelationship(childId, parent1Id, parent2Id) {
  const params = {
    childId,
    parent1Id,
    parent2Id,
  }
  return axios.post(`${BASE_PERSONS_PATH}/tree-entity/parent-child-relationship`, null, { params })
}

export function createAssociationRelationship(associationType, person1Id, person2Id, reason) {
  const params = {
    associationType,
    person1Id,
    person2Id,
    reason,
  }
  return axios.post(`${BASE_PERSONS_PATH}/tree-entity/association`, null, { params })
}

export async function invalidateOrdinanceCache(personId) {
  return axios.put(`${BASE_PERSONS_PATH}/invalidations/persons/${personId}`)
}

export async function getPersonOwnership(personId) {
  // should pick 1. creatorCisId: 'cis.user.MMMM-ZJPT', and stop at that
  // .a not wkc, has username. You are the chosen one: cis.user.MMMM-ZJPT
  // return {
  //   personId: 'G942-CCC',
  //   accessControl: {
  //     spaceId: 'MMMM-MMM',
  //     hidden: false,
  //     locked: false,
  //     living: false,
  //     ownerRestricted: false,
  //     hideCompletedOrdinances: false,
  //     ordinanceApprovalRequired: false,
  //   },
  //   creatorCisId: 'cis.user.MMMM-ZJPT',
  //   contributorCisIds: ['cis.user.MMMM-DP93', 'cis.user.MMS2-GK7F'],
  //   oneHopOwnerCisIds: [],
  //   oneHopOwningGroupIds: [],
  //   copiedToOwnerCisIds: [],
  //   copiedToGroupIds: [],
  // }

  // should pick creatorGroup id
  // 1. creatorGroupId. You are the chosen one: MMMM-FDW
  // return {
  //   personId: 'BM7D-PRF',
  //   accessControl: {
  //     spaceId: '9MMM-MFZF',
  //     ownerGroupId: 'MMMM-FZF',
  //     hidden: false,
  //     locked: false,
  //     living: true,
  //     ownerRestricted: false,
  //     hideCompletedOrdinances: true,
  //     ordinanceApprovalRequired: true,
  //   },
  //   creatorGroupId: 'MMMM-FDW',
  //   contributorCisIds: ['cis.user.MMMM-VQWD'],
  //   oneHopOwnerCisIds: [],
  //   oneHopOwningGroupIds: ['MMMM-FDW', 'MMMM-FZF'],
  //   copiedFromPersonId: 'KWHK-7XQ',
  //   copiedToOwnerCisIds: [],
  //   copiedToGroupIds: [],
  // }

  // 2.a should pick second oneHopOwnerCisId cis.user.MMMM-ZJPT
  // 2.a not wkc, has username. You are the chosen one: cis.user.MMMM-ZJPT
  // return {
  //   personId: 'G942-CCC',
  //   accessControl: {
  //     spaceId: 'MMMM-MMM',
  //     hidden: false,
  //     locked: false,
  //     living: false,
  //     ownerRestricted: false,
  //     hideCompletedOrdinances: false,
  //     ordinanceApprovalRequired: false,
  //   },
  //   creatorCisId: 'cis.user.MMM4-5MH9',
  //   contributorCisIds: [],
  //   oneHopOwnerCisIds: ['cis.user.MMM4-5MH9', 'cis.user.MMMM-ZJPT', 'cis.user.MMMM-DP93', 'cis.user.MMS2-GK7F'],
  //   oneHopOwningGroupIds: [],
  //   copiedToOwnerCisIds: [],
  //   copiedToGroupIds: [],
  // }

  // 2.group
  // return {
  //   personId: 'BM7D-PRF',
  //   accessControl: {
  //     spaceId: '9MMM-MFZF',
  //     ownerGroupId: 'MMMM-FZF',
  //     hidden: false,
  //     locked: false,
  //     living: true,
  //     ownerRestricted: false,
  //     hideCompletedOrdinances: true,
  //     ordinanceApprovalRequired: true,
  //   },
  //   creatorGroupId: 'MMMM-FZF',
  //   contributorCisIds: ['cis.user.MMMM-VQWD'],
  //   oneHopOwnerCisIds: [],
  //   oneHopOwningGroupIds: ['MMMM-FZF', 'MMMM-FDW'],
  //   copiedFromPersonId: 'KWHK-7XQ',
  //   copiedToOwnerCisIds: [],
  //   copiedToGroupIds: [],
  // }

  // should pick 3. contributorCisIds
  // 3.a contributorCisIds not wkc, has username. You are the chosen one: cis.user.MMMM-ZJPT
  // return {
  //   personId: 'G942-CCC',
  //   accessControl: {
  //     spaceId: 'MMMM-MMM',
  //     hidden: false,
  //     locked: false,
  //     living: false,
  //     ownerRestricted: false,
  //     hideCompletedOrdinances: false,
  //     ordinanceApprovalRequired: false,
  //   },
  //   creatorCisId: 'cis.user.MMM4-5MH9',
  //   contributorCisIds: ['cis.user.MMM4-5MH9', 'cis.user.MMMM-ZJPT', 'cis.user.MMMM-DP93', 'cis.user.MMS2-GK7F'],
  //   oneHopOwnerCisIds: [],
  //   oneHopOwningGroupIds: [],
  //   copiedToOwnerCisIds: [],
  //   copiedToGroupIds: [],
  // }

  if (personId) {
    const response = await axios.get(`${BASE_PERSONS_PATH}/${personId}/ownership`)
    return response.status >= 200 && response.status < 300 ? response.data : null
  }
  return null
}

export function copyPersonToOwners(personId, cisIds, treeIds) {
  let url = `${BASE_PERSONS_PATH}/${personId}/copyToOwners?`
  if (cisIds && cisIds.length > 0) {
    url += `&cisId=${cisIds.join('&cisId=')}`
  }
  if (treeIds && treeIds.length > 0) {
    url += `&groupId=${treeIds.join('&treeId=')}`
  }
  return axios
    .post(url)
    .then((response) => response.data)
    .catch((error) => error.response?.data)
}

export function getLabelAssignments(labelValue, pagingLimit, startAdminLabelId) {
  let url = `${BASE_PERSONS_PATH}/labels/${labelValue}?pagingLimit=${pagingLimit}`
  if (startAdminLabelId) {
    url += `&startAdminLabelId=${startAdminLabelId}`
  }
  return axios
    .get(url)
    .then((response) => response.data)
    .catch((error) => error.response?.data)
}
